import { useQueries, useQuery } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'
import { nonNullable } from 'lib/array/arrayUtils'
import { generateMustDoActivities, generateThemesV2 } from 'tripPlanner/api'
import { MustDoActivityItem } from './mustDoActivities'
import * as ThemeKeys from '../reactQueryKeys/themes'
import * as MustDoActivityKeys from '../reactQueryKeys/mustDoActivities'

export interface ThemeItem {
  theme: string
  description: string
}

export interface LocationForGeneration {
  locationName: string
  destinationId?: string
}

export interface TripGenerationData {
  themes: Array<ThemeItem> | undefined
  mustDoActivitiesByLocation: Map<string, Array<MustDoActivityItem>>
  isLoading: boolean
  themesLoading: boolean
  mustDoLoading: boolean
}

function areMapsEqual<K, V>(map1: Map<K, V>, map2: Map<K, V>): boolean {
  if (map1 === map2) return true
  if (map1.size !== map2.size) return false
  for (const [key, value] of map1) {
    if (!map2.has(key) || map2.get(key) !== value) return false
  }
  return true
}

export const useTripGenerationData = (
  { locations, region }: {
    locations: Array<LocationForGeneration>
    region?: string
  },
): TripGenerationData => {
  const locationsWithDestId = locations.filter(loc => loc.destinationId && loc.locationName)
  const locationIds = nonNullable(locationsWithDestId.map(loc => loc.destinationId))
  const hasValidParams = locationIds.length > 0 && !!region

  const themesQuery = useQuery({
    queryKey: ThemeKeys.themesV2(locationIds, region),
    queryFn: () => generateThemesV2({ locationIds, region }),
    enabled: hasValidParams,
  })

  const mustDoQueries = useQueries({
    queries: locationsWithDestId.map((location) => ({
      queryKey: MustDoActivityKeys.mustDoActivitiesSingle(location.destinationId!, region),
      queryFn: async() => {
        const activities = await generateMustDoActivities({
          locationId: location.destinationId!,
          region,
        })
        return { locationName: location.locationName, activities: activities ?? [] }
      },
      enabled: hasValidParams,
    })),
  })

  const mustDoMapRef = useRef<Map<string, Array<MustDoActivityItem>>>(new Map())

  const queryResults = useMemo(() =>
    nonNullable(mustDoQueries.map(q => q.data))
      .filter(data => data.activities.length > 0),
  [mustDoQueries])

  const mustDoActivitiesByLocation = useMemo(() => {
    const map = new Map<string, Array<MustDoActivityItem>>()
    queryResults.forEach((data) => {
      map.set(data.locationName, data.activities)
    })

    if (!areMapsEqual(mustDoMapRef.current, map)) {
      mustDoMapRef.current = map
    }
    return mustDoMapRef.current
  }, [queryResults])

  const mustDoLoading = mustDoQueries.some(q => q.isLoading)

  return {
    themes: themesQuery.data,
    mustDoActivitiesByLocation,
    isLoading: themesQuery.isLoading || mustDoLoading,
    themesLoading: themesQuery.isLoading,
    mustDoLoading,
  }
}
